/* URL routing on the History API, with the View Transitions API between pages where the browser has it
   (and the reader has not asked for reduced motion). */
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import { flushSync } from 'react-dom'

const listeners = new Set<() => void>()
const subscribe = (fn: () => void) => {
  listeners.add(fn)
  window.addEventListener('popstate', fn)
  return () => {
    listeners.delete(fn)
    window.removeEventListener('popstate', fn)
  }
}
const snapshot = () => window.location.pathname + window.location.search

export function useLocation(): { path: string; query: URLSearchParams } {
  const loc = useSyncExternalStore(subscribe, snapshot, () => '/')
  const [path, q = ''] = loc.split('?')
  return { path, query: new URLSearchParams(q) }
}

type Transition = { ready: Promise<void>; finished: Promise<void>; updateCallbackDone: Promise<void> }

/** Runs an update inside a view transition where the browser has one. A transition interrupted by the
    next one is not an error, so its promises are quietly settled. */
let transitions = true
/** The licensee console calls this once: its chrome never animates, and a view transition would cross-fade the whole page, chrome included. */
export function setViewTransitions(on: boolean): void {
  transitions = on
}

export function withTransition(update: () => void): void {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => Transition }
  if (!transitions || !doc.startViewTransition) return update()
  const t = doc.startViewTransition(update)
  for (const p of [t.ready, t.finished, t.updateCallbackDone]) p.catch(() => undefined)
}

const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === snapshot()) return
  const go = () => {
    if (opts.replace) window.history.replaceState(null, '', to)
    else window.history.pushState(null, '', to)
    flushSync(() => listeners.forEach((l) => l()))
    if (!opts.replace) window.scrollTo({ top: 0 })
  }
  if (!reduced() && !opts.replace) withTransition(go)
  else go()
}

/** Matches "/runs/:id" against a path; null when it does not match. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const keys: string[] = []
  const re = new RegExp(`^${pattern.replace(/:([a-z]+)/g, (_, k: string) => (keys.push(k), '([^/]+)'))}/?$`)
  const m = re.exec(path)
  return m ? Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) : null
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; children: ReactNode; current?: boolean }

export function Link({ to, children, current, onClick, ...rest }: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    navigate(to)
  }
  return (
    <a href={to} onClick={handle} aria-current={current ? 'page' : undefined} {...rest}>
      {children}
    </a>
  )
}
